import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { useTranslation } from 'next-i18next'

import todAnalytics from '~/analytics/tod/todAnalytics'
import { TodButtonEventName } from '~/analytics/tod/todEvents.types'

import ROUTES from '~/shared/constants/routes.const'
import { isAuthenticated } from '~/auth/slices/auth.slice'
import { setIsOpenActionCreator } from '~/redux/slices/chat'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { useGetProducts } from '~/booking/hooks/useGetProduct'
import appConfig from '~/config/app'

import useSubscriptionNeededForUser from '~/hooks/useSubscriptionNeededForUser'

import ProductCardButton from './components/ProductCardButton/ProductCardButton.component'
import { ProductCard } from './components/ProductCard.types'
import { i18nifyHighlights, getPriceWithCurrency } from './TutorOnDemandProductCards.utils'

const VIDEO_BASE_PATH = `${appConfig.assetsCdnUrl}/videos`

interface configCards {
  hideVideoChatWithTutor?: boolean
  hideChatWithTutor?: boolean
  showInSlider?: boolean
  colorSchemeIsWhite?: boolean
}

export default function useTutorOnDemandProductCards({
  hideChatWithTutor = false,
  colorSchemeIsWhite = true,
}: configCards): ProductCard[] {
  const isSubscriptionNeeded = useSubscriptionNeededForUser()
  const { t } = useTranslation(['generics', 'tutorOnDemand'])
  const { data: bookingProducts } = useGetProducts()
  const authenticated = useAppSelector(isAuthenticated)
  const dispatch = useAppDispatch()
  const router = useRouter()

  const productPrices = useMemo(() => {
    if (bookingProducts) {
      const { lowestHourlyPriceProduct, videoChatProduct } = bookingProducts
      if (!lowestHourlyPriceProduct || !videoChatProduct) return
      const lowestHourlyPriceLabel = getPriceWithCurrency(
        lowestHourlyPriceProduct.currency,
        lowestHourlyPriceProduct.priceHourly,
      )
      const videoChatPriceLabel = getPriceWithCurrency(videoChatProduct.currency, videoChatProduct.price)
      return { lowestHourlyPriceLabel, videoChatPriceLabel }
    }
  }, [bookingProducts])

  const handleOnChatButtonClick = (): void => {
    todAnalytics.logButtonClick(TodButtonEventName.CHAT_WITH_TUTOR)

    if (isSubscriptionNeeded) {
      router.push(ROUTES.SUBSCRIPTION_PAGE)
    } else if (authenticated) {
      dispatch(setIsOpenActionCreator(true))
    } else {
      router.push(ROUTES.REGISTER, { query: { from: router.asPath } })
    }
  }

  const chatWithTutorCard: ProductCard = {
    button: (
      <ProductCardButton
        label={isSubscriptionNeeded ? t('generics:subscribe') : t('generics:chat')}
        onClick={handleOnChatButtonClick}
      />
    ),
    highlights: i18nifyHighlights(t, 'chat'),
    icon: 'hom-chat-circle',
    price: t(`tutorOnDemand:landingPage.productCard.chat.${isSubscriptionNeeded ? 'pricePilot' : 'price'}`),
    title: t('tutorOnDemand:landingPage.productCard.chat.title'),
    video: `${VIDEO_BASE_PATH}/mod-product-chat`,
    videoFallback: colorSchemeIsWhite
      ? 'mentor-on-demand/chat-with-mentor-poster-whitev2.png'
      : 'mentor-on-demand/chat-with-mentor-poster-bluev2.png',
    videoFallbackHeight: 422,
    videoFallbackWidth: 446,
  }

  const personalTutorCard: ProductCard = {
    button: <ProductCardButton href={ROUTES.BOOKING_FOR_SELF} label={t('generics:book')} />,
    highlights: i18nifyHighlights(t, 'tutoring'),
    icon: 'hom-graduation-cap-circle',
    price: t('tutorOnDemand:landingPage.productCard.privateTutor.price', {
      price: productPrices?.lowestHourlyPriceLabel ?? ' - ',
    }),
    title: t('tutorOnDemand:landingPage.productCard.privateTutor.title'),
    video: `${VIDEO_BASE_PATH}/mod-product-tutoring`,
    videoCx: 'w-full object-cover',
    videoFallback: colorSchemeIsWhite
      ? 'mentor-on-demand/personal-mentor-poster-whitev2.png'
      : 'mentor-on-demand/personal-mentor-poster-bluev2.png',
    videoFallbackHeight: 422,
    videoFallbackWidth: 500,
  }

  let productCards: ProductCard[] = [personalTutorCard]

  if (!hideChatWithTutor) {
    productCards = [...productCards, chatWithTutorCard]
  }

  return productCards
}
